import * as React from 'react';
import Stack from '@mui/material/Stack';
import { PieChart } from '@mui/x-charts/PieChart';
import { Card, Skeleton } from '@mui/material';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

const colors = [
    '#4CAF50',      // Green
    '#2196F3',      // Blue
    '#00BCD4',      // Cyan
    '#009688',      // Teal
    '#3F51B5',      // Indigo
    '#FF9800',      // Orange
    '#8BC34A',      // Light Green
];

export default function BatteryRange({ chartData, loading }) {
    const theme = useTheme();
    const batteryRangeData = chartData || [];

    // Calculate total for percentages
    const total = batteryRangeData.reduce((sum, item) => sum + item.count, 0);

    // Calculate average range
    const averageRange = total ? batteryRangeData.reduce((acc, item) => {
        const [min, max] = item.range.split('-').map(Number);
        const middleValue = (min + max) / 2;
        return acc + (middleValue * item.count);
    }, 0) / total : 0;

    // Format data for PieChart
    const data = batteryRangeData.map((item, index) => ({
        label: `${item.range} mi`,
        value: item.count,
        color: colors[index],
        percentage: ((item.count / total) * 100).toFixed(1)
    }));



    return (
        <Card variant="outlined">
            <CardContent>
                <Typography component="h2" variant="subtitle1" gutterBottom fontWeight="medium">
                    Battery Range Distribution
                </Typography>
                <Stack direction="column" spacing={2} alignItems="center">
                    <Box position="relative" width="100%" maxWidth={320} height={170} display="flex" justifyContent="center" alignItems="center">
                        {loading ? (
                            <Skeleton variant="circular" width={300} height={150} sx={{ transform: 'none', bgcolor: theme.palette.background.paper, borderRadius: '200px 200px 0 0' }} />
                        ) : (
                            <PieChart
                                series={[
                                    {
                                        startAngle: -90,
                                        endAngle: 90,
                                        paddingAngle: 1,
                                        innerRadius: 80,
                                        outerRadius: 120,
                                        data,
                                        colors: colors,
                                        cx: 150,
                                        cy: 160,
                                    },
                                ]}
                                width={300}
                                height={170}
                                slotProps={{
                                    legend: { hidden: true },
                                }}
                            />
                        )}
                        {/* Center text overlay */}
                        <Box
                            position="absolute"
                            top="72%"
                            left="50%"
                            sx={{
                                transform: 'translate(-50%, -50%)',
                                textAlign: 'center'
                            }}
                        >
                            {loading ? (
                                <Stack alignItems="center" spacing={1}>
                                    <Skeleton variant="text" width={80} height={40} />
                                    <Skeleton variant="text" width={60} />
                                </Stack>
                            ) : (
                                <>
                                    <Typography variant="h4" component="div">
                                        {Math.round(averageRange)} mi
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Avg Range
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Box>

                    {/* Legend */}
                    <Box
                        sx={{
                            width: '100%',
                            display: 'grid',
                            gap: 1,
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            alignItems: 'start',
                        }}
                    >
                        {loading ? (
                            Array.from(new Array(4)).map((_, index) => (
                                <Box key={index}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Skeleton variant="circular" width={12} height={12} />
                                        <Skeleton variant="text" width={100} />
                                    </Stack>
                                </Box>
                            ))
                        ) : (
                            data.map((item, index) => (
                                <Box key={item.label}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: colors[index],
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                                            {item.label} <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{item.percentage}%</Box>
                                        </Typography>
                                    </Stack>
                                </Box>
                            ))
                        )}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}
